import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { RequestStatus } from '@domain/vocabulary';
import {
  AWAITING_INCAPACITY_STATUSES,
  AWAITING_PERMISSION_STATUSES,
  ROLE_LABEL,
} from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import { formatDateRange, formatDayWithYear } from '../format';
import { Banner, Button, cssVars, cx } from '../design-system';
import { useRuntimeConfigState } from '../providers/RuntimeConfigProvider';
import { useSession, useSessionState } from '../providers/SessionProvider';
import { ConfirmDataGate } from '../screens/Onboarding/ConfirmDataGate';
import { BirthdayGreeting } from '../screens/Blog/BirthdayGreeting';
import { AreaFocusPicker } from './AreaFocusPicker';
import { expiringLive, newestLive } from '../balancePeriod';
import { useLiveBalances } from '../hooks/useLiveBalances';
import { useAbsenceScope } from '../hooks/useAbsenceScope';
import { useLiveRequests, type Subscribe } from '../hooks/useLiveRequests';
import { useAccess } from '../hooks/useAccess';
import { reloadWithoutCache, useNewDeployment } from '../hooks/useNewDeployment';
import { reloadShortcut } from '../deployment';
import { PRODUCT_NAME } from '../brand';
import { placeOf, useApprovalScope } from '../hooks/useApprovalScope';
import { useAreaName, useOwnAreaName } from '../hooks/useAreaName';
import { usePageViews } from '../hooks/usePageViews';
import { useSubAreaName } from '../hooks/useSubAreaName';
import { useToast } from '../providers/ToastProvider';
import { DemoRoleSwitcher } from './DemoRoleSwitcher';
import { PageTitleSlotContext } from './PageTitle';
import { SessionAvatar } from './SessionAvatar';
import {
  covers,
  entryFor,
  findDestination,
  sidebarFor,
  usesFullWidth,
  visibleNavigation,
  type NavBadgeKey,
  type NavDestination,
} from './navigation';
import styles from './AppShell.module.css';
import logoUrl from '../assets/knLogo.png';

/**
 * The application shell.
 *
 * Dark sidebar with the balance card at its foot, a search field and role chip in
 * the top bar, and a separate dark header on mobile — all transcribed from the
 * prototype rather than approximated.
 *
 * Two behaviours the handoff's responsive rules require: below 900px the sidebar
 * becomes a drawer that closes on navigation, Escape and backdrop click; and the
 * navigation is derived from the live permission matrix on every render, so
 * switching off a module removes its section without a reload.
 */

/** Query families that show the viewer's own absences, directly or derived. */
const OWN_QUERY_KEYS: readonly (readonly string[])[] = [
  ['requests'],
  ['absences'],
  ['home'],
  ['shell'],
];

/** Query families that show a decision queue. */
const QUEUE_QUERY_KEYS: readonly (readonly string[])[] = [['approvals'], ['home'], ['shell']];

/**
 * What the leader's live queue watches.
 *
 * The three columns still awaiting a decision, plus «Cancelada» — which is not a
 * column and never appears on the board. It is in the *subscription* so that an
 * employee cancelling reads as a transition the leader can be told about, rather
 * than as a document silently vanishing from the result set. A request the leader
 * decides themselves leaves the set instead, and refreshes the board without
 * announcing anything: they were the one who did it.
 */
const PENDING_STATUSES: readonly RequestStatus[] = [
  'Por revisar',
  'En revisión',
  'Visto bueno',
  'Cancelada',
];

/** The bell is a glance, not an inbox. Older entries fall off the bottom. */
const MAX_NOTIFICATIONS = 8;

export const AppShell = (): ReactNode => {
  const session = useSession();

  /* Una vista por pantalla. Aquí porque es por donde pasan las veinte. */
  usePageViews();

  const { signOut } = useSessionState();
  const { needsReload, error: configError } = useRuntimeConfigState();
  const newDeployment = useNewDeployment();
  const { holds, featureOn } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const services = getServices();

  const approval = useApprovalScope();

  const [navOpen, setNavOpen] = useState(false);
  /* El hueco de la barra donde sube el título de la pantalla. Ver `PageTitle`. */
  const [titleNode, setTitleNode] = useState<HTMLElement | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>(() =>
    services.isDemo
      ? [
          {
            id: 'expiry',
            kind: 'warning',
            title: '6 días de vacaciones vencen el 30 nov',
            text: 'Agenda esos días antes de que caduquen.',
            time: 'Hace 2 h',
            read: false,
          },
          {
            id: 'replacement',
            kind: 'success',
            title: 'Bruno Chávez te designó como su reemplazo',
            text: '20 jul – 24 jul 2026 · Desarrollo iOS',
            time: 'Ayer',
            actionable: true,
            read: false,
          },
          {
            id: 'status',
            kind: 'brand',
            title: 'Tu solicitud VC-0192 fue aprobada',
            text: '6 jul – 10 jul 2026',
            time: 'Hace 2 días',
            // Ya leída: es la que enseña cómo se ve una sin resalte.
            read: true,
          },
        ]
      : [],
  );
  const welcomeShown = useRef(false);

  const unreadCount = notifications.filter((item) => !item.read).length;

  /*
   * Live, from both sides of the desk, and mounted here rather than on a screen:
   * the person it concerns may be anywhere in the portal, and the badge in this
   * very sidebar is one of the things that has to change.
   *
   * Announced as well as refreshed. A board that silently rearranges itself is
   * unsettling — the leader who was reading a card watches it move for no visible
   * reason — so anything that arrives or changes hands says so once, and the
   * notification stays in the bell for whoever was not looking.
   */
  const announce = useCallback(
    (item: Omit<NotificationItem, 'time' | 'read'>): void => {
      setNotifications((current) =>
        current.some((existing) => existing.id === item.id)
          ? current
          : [{ ...item, time: 'Ahora', read: false }, ...current].slice(0, MAX_NOTIFICATIONS),
      );
    },
    [],
  );

  useLiveRequests({
    subscribe: useCallback<Subscribe>(
      (onChange, onError) =>
        getServices().requests.subscribeToPerson(session.personId, onChange, onError),
      [session.personId],
    ),
    invalidate: OWN_QUERY_KEYS,
    onChanges: (changes) => {
      for (const { request, to } of changes.moved) {
        announce({
          id: `own-${request.id}-${to}`,
          kind: to === 'Rechazada' ? 'warning' : 'success',
          title: `${request.folio} pasó a «${to}»`,
          text: formatDateRange(request.range),
        });
        toast.ok(`${request.folio} pasó a «${to}».`);
      }
    },
  });

  useLiveRequests({
    // Only for somebody with a queue. An employee has nothing to watch here, and
    // subscribing them would be a standing read of the whole sub area.
    subscribe: useMemo<Subscribe | null>(
      () =>
        holds('aprobar')
          ? (onChange, onError) =>
              getServices().requests.subscribeToScope(
                { ...approval.scope, statuses: PENDING_STATUSES },
                onChange,
                onError,
              )
          : null,
      [holds, approval.scope],
    ),
    invalidate: QUEUE_QUERY_KEYS,
    onChanges: (changes) => {
      for (const { request } of changes.arrived) {
        // The subscription watches the whole area; the announcement is only for
        // the people this leader actually signs for.
        if (!approval.covers(request.personId)) continue;
        announce({
          id: `queue-${request.id}`,
          kind: 'brand',
          title: `Nueva solicitud de ${request.personName}`,
          text: `${formatDateRange(request.range)} · ${request.folio}`,
          /*
           * Sin botones. Los que trae `actionable` dicen «Aceptar reemplazo» y
           * «No puedo», que son la respuesta a que te designen respaldo — no a
           * que alguien pida vacaciones. A un líder en producción le aparecían
           * dos botones que no correspondían y que sólo mostraban un aviso.
           * Decidir esta solicitud se hace en Aprobaciones, que es donde están
           * las firmas.
           */
        });
        toast.ok(`Nueva solicitud de ${request.personName}.`);
      }

      // Worth interrupting for: it is the one change that means «deja de
      // revisarla». Everything else that leaves the queue left because the
      // leader decided it, and telling them so is noise.
      for (const { request, to } of changes.moved) {
        if (to !== 'Cancelada' || !approval.covers(request.personId)) continue;
        announce({
          id: `queue-cancel-${request.id}`,
          kind: 'warning',
          title: `${request.personName} canceló ${request.folio}`,
          text: formatDateRange(request.range),
        });
        toast.ok(`${request.personName} canceló ${request.folio}.`);
      }
    },
  });

  useEffect(() => {
    if (welcomeShown.current || !services.isDemo || !holds('solicitar')) return;
    welcomeShown.current = true;
    toast.info('Bienvenido, Marco\nTienes 3 notificaciones sin leer.');
  }, [services.isDemo, holds, toast]);

  const closeNav = useCallback(() => {
    setNavOpen(false);
  }, []);

  // Navigating on a phone should reveal the destination, not leave the drawer over it.
  useEffect(() => {
    closeNav();
  }, [location.pathname, closeNav]);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeNav();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [navOpen, closeNav]);

  /* Todos sus periodos vigentes: con «Vencimiento del saldo» en más de 12 meses,
     lo que quede del anterior sigue valiendo junto al nuevo. */
  const balances = useLiveBalances({
    scope: 'shell',
    personId: session.personId,
    hireDate: session.hireDate,
    expiryMonths: session.config.rules.expiryMonths,
    enabled: holds('solicitar') || holds('perfil'),
  });
  const lives = useMemo(() => balances.data ?? [], [balances.data]);
  const currentLive = newestLive(lives);
  const olderLives = lives.filter((live) => live !== currentLive);

  const isSuper = session.role === 'super';
  /* Quien no tiene el área por frontera. Espejo de `seesEveryArea` en el servidor. */
  const seesEveryArea = session.role === 'super' || session.role === 'global';
  const absences = useAbsenceScope();

  /**
   * Los días que se pierden pronto, si toca avisar.
   *
   * Misma cuenta que la cabecera de Inicio y la misma ventana: si las dos
   * hablaran de momentos distintos, la barra lateral estaría contradiciendo a la
   * pantalla que la acompaña.
   */
  const expiring = useMemo(
    () => expiringLive(lives, session.hireDate, getServices().clock.today()),
    [lives, session.hireDate],
  );

  const searching = location.pathname === '/buscar';

  /* Mirrors the URL so the field survives a reload and a shared link. */
  const searchTerm = searching ? (new URLSearchParams(location.search).get('q') ?? '') : '';

  /**
   * The counts the sidebar pills show.
   *
   * Each is a server-side count rather than a list length — at 500 people the
   * difference is a handful of reads against several thousand — and each is gated
   * on the permission or feature that makes it meaningful, so an employee never
   * queries the area's queue just to render a pill they will not see.
   *
   * The two pills answer two different questions, and keeping them apart is the
   * point: «Ausencias» is *what I asked for and have not heard back on*, and
   * «Aprobaciones» is *what others are waiting on me for*. Both used to count only
   * vacations, which left a leader with three permits pending and no number
   * anywhere in the menu.
   */
  const ownPending = useQuery({
    queryKey: ['shell', 'badge', 'ausencias', session.personId],
    enabled: holds('solicitar'),
    queryFn: async () => {
      const result = await getServices().requests.count({
        personId: session.personId,
        statuses: ['Por revisar', 'En revisión', 'Visto bueno'],
      });
      return result.ok ? result.value : 0;
    },
  });

  /*
   * Scoped with `useAbsenceScope`, the same hook the Ausencias screens use, so the
   * pill and the list it leads to count the same documents. When those two rules
   * drifted apart before, an administrator saw a badge of three over an empty
   * list — the badge was right and the list was scoped to a person who files
   * nothing.
   */
  const ownPermits = useQuery({
    queryKey: ['shell', 'badge', 'permisos', absences.scope],
    enabled: holds('solicitar') && featureOn('permisos'),
    queryFn: async () => {
      const result = await getServices().permissions.count({
        ...absences.scope,
        statuses: AWAITING_PERMISSION_STATUSES,
      });
      return result.ok ? result.value : 0;
    },
  });

  const ownCertificates = useQuery({
    queryKey: ['shell', 'badge', 'incapacidades', absences.scope],
    enabled: holds('solicitar') && featureOn('incapacidades'),
    queryFn: async () => {
      const result = await getServices().incapacities.count({
        ...absences.scope,
        statuses: AWAITING_INCAPACITY_STATUSES,
      });
      return result.ok ? result.value : 0;
    },
  });

  /*
   * The approval side of the same two collections.
   *
   * Certificates are counted only for RR.HH.: `validateIncapacity` refuses anyone
   * who is not an administrator, so a leader's pill must not include work they
   * would have no button for.
   */
  const queuePermits = useQuery({
    queryKey: ['shell', 'badge', 'colaPermisos', approval.scope, session.personId],
    enabled: holds('aprobar') && featureOn('permisos') && !approval.loading,
    queryFn: async () => {
      // Same split as the vacation pill above, for the same reason.
      if (!approval.excludesOwn) {
        const result = await getServices().permissions.count({
          ...approval.scope,
          statuses: AWAITING_PERMISSION_STATUSES,
        });
        return result.ok ? result.value : 0;
      }

      const result = await getServices().permissions.list(
        { ...placeOf(approval.scope), statuses: AWAITING_PERMISSION_STATUSES },
        { limit: 100 },
      );
      return result.ok ? approval.mine(result.value.items).length : 0;
    },
  });

  const queueCertificates = useQuery({
    queryKey: ['shell', 'badge', 'colaIncapacidades', approval.scope],
    enabled: holds('admin') && featureOn('incapacidades'),
    queryFn: async () => {
      const result = await getServices().incapacities.count({
        ...approval.scope,
        statuses: AWAITING_INCAPACITY_STATUSES,
      });
      return result.ok ? result.value : 0;
    },
  });

  const queuePending = useQuery({
    // Keyed on the resolved scope, not the area: two leaders of the same area
    // lead different people and must not share a cached count.
    queryKey: ['shell', 'badge', 'aprobaciones', approval.scope, session.personId],
    enabled: holds('aprobar') && !approval.loading,
    queryFn: async () => {
      const statuses = ['Por revisar', 'En revisión', 'Visto bueno'] as const;

      /*
       * An administrator's pill is a server-side aggregate: the whole area is
       * exactly what they decide on, so nothing has to come down to be counted.
       *
       * A leader's is not, and cannot be. Who they approve is a list of people
       * kept in `personLeaders`, and an aggregate can only count what a query
       * can express — no query says «these twenty-one». The documents come down
       * and the pill counts what survives the filter, which is the same rule the
       * board draws and the server enforces.
       */
      if (!approval.excludesOwn) {
        const result = await getServices().requests.count({ ...approval.scope, statuses });
        return result.ok ? result.value : 0;
      }

      const result = await getServices().requests.list(
        { ...approval.scope, statuses },
        { limit: 100 },
      );
      return result.ok ? approval.mine(result.value.items).length : 0;
    },
  });

  const pendingAccounts = useQuery({
    queryKey: ['shell', 'badge', 'cuentas', session.areaId],
    enabled: holds('admin'),
    queryFn: async () => {
      const result = await getServices().people.countByStatus(session.areaId, 'pending');
      return result.ok ? result.value : 0;
    },
  });

  const badgeCounts: Readonly<Record<NavBadgeKey, number>> = {
    ausencias: (ownPending.data ?? 0) + (ownPermits.data ?? 0) + (ownCertificates.data ?? 0),
    aprobaciones:
      (queuePending.data ?? 0) + (queuePermits.data ?? 0) + (queueCertificates.data ?? 0),
    cuentas: pendingAccounts.data ?? 0,
  };

  const blocks = sidebarFor(
    visibleNavigation({ holds, featureOn, isSuper, crossArea: seesEveryArea }),
  );
  /* La entrada en la que se está, y sus pestañas si es una sección. */
  const section = entryFor(location.pathname, blocks);
  const sectionTabs = section !== undefined && section.tabs.length > 1 ? section.tabs : [];
  const countOf = (destination: NavDestination): number =>
    destination.badge === undefined ? 0 : badgeCounts[destination.badge];
  const current = findDestination(location.pathname);
  const roleLabel = ROLE_LABEL[session.role];

  /*
   * Con la búsqueda global apagada, su sitio en la barra lo ocupa el título de la
   * pantalla. Lo pone la propia pantalla —ver `PageTitle`—; esto es lo que sale
   * cuando la pantalla no lo sube: el organigrama, que se queda con su título
   * porque tiene pantalla completa, y la ficha de una persona, cuyo `h1` es el
   * nombre y va junto a su foto. Sin esto la barra volvería a quedar vacía justo
   * en esas dos.
   */
  const titleInBar = !featureOn('busqueda');
  const titleSlot = useMemo(
    () => ({ inBar: titleInBar, node: titleNode }),
    [titleInBar, titleNode],
  );
  const fallbackTitle =
    section?.tabs.find((tab) => covers(tab.path, location.pathname))?.label ??
    (location.pathname.startsWith('/persona/') ? 'Ficha de persona' : null);

  /*
   * The sidebar states organisational placement, not the role — the top bar
   * already carries the role chip, and repeating it here wastes the one line
   * that could say which area and sub area the person belongs to.
   */
  const areaName = useAreaName();
  /* El rótulo de identidad usa **su** área, no la enfocada: dice quién es alguien,
     no qué está mirando. Ver `useOwnAreaName`. */
  const subAreaName = useSubAreaName(useOwnAreaName());
  const brandScope = isSuper ? 'Sistema · global' : areaName;
  const userScope = isSuper ? 'Superusuario · sin área' : subAreaName;

  return (
    <div className={styles.shell}>
      {navOpen && (
        <button
          type="button"
          className={styles.backdrop}
          onClick={closeNav}
          aria-label="Cerrar el menú"
        />
      )}

      <nav
        className={cx(styles.sidebar, navOpen && styles.sidebarOpen)}
        aria-label="Navegación principal"
      >
        <div className={styles.brand}>
          <img src={logoUrl} alt="" className={styles.logo} />
          <div className={styles.brandText}>
            <div className={styles.brandName}>{PRODUCT_NAME}</div>
            <div className={styles.brandScope}>{brandScope}</div>
          </div>
        </div>

        <div className={styles.navGroups}>
          {/*
           * La búsqueda, mientras dura, tiene su propia entrada en el menú.
           *
           * `/buscar` es una pantalla como las demás, pero es la única a la que se
           * llega sin tocar el menú —se escribe arriba y la app navega—, así que
           * ninguna entrada queda marcada y el menú deja de decir dónde estás.
           * Quien no encuentre «Salir de la búsqueda» se queda sin salida obvia,
           * teniendo el menú entero delante.
           *
           * Aparece marcada como la sección actual, así que el menú vuelve a
           * responder «aquí», y cualquier otra entrada devuelve a lo suyo — que
           * era la salida que ya existía y no se veía. Se va sola al navegar: no
           * hay nada que cerrar.
           */}
          {searching && (
            <div className={cx(styles.navGroup, styles.navGroupTransient)}>
              <NavLink
                to={{ pathname: '/buscar', search: location.search }}
                className={({ isActive }) => cx(styles.navItem, isActive && styles.navItemActive)}
              >
                <span className={styles.navDot} aria-hidden="true" />
                <span className={styles.navLabel}>Búsqueda global</span>
              </NavLink>
            </div>
          )}

          {blocks.map((block) => (
            <div key={block.label} className={styles.navGroup}>
              <span className={styles.navGroupLabel}>{block.label}</span>
              {block.entries.map((entry) => {
                /* Una sección lleva la suma de lo que espera en sus pestañas. */
                const count = entry.tabs.reduce((sum, tab) => sum + countOf(tab), 0);
                const isSection = entry.tabs.length > 1;
                /*
                 * Marcada por dónde se está, no por a dónde lleva: la entrada de
                 * una sección apunta a su primera pestaña y tiene que seguir
                 * encendida en las demás.
                 */
                const active = entry === section;

                return (
                  <Link
                    key={entry.path}
                    to={entry.path}
                    className={cx(styles.navItem, active && styles.navItemActive)}
                    aria-current={active ? (isSection ? 'true' : 'page') : undefined}
                    title={isSection ? entry.tabs.map((tab) => tab.label).join(' · ') : undefined}
                  >
                    <span className={styles.navDot} aria-hidden="true" />
                    <span className={styles.navLabel}>{entry.label}</span>
                    {/* A zero is not news; the pill appears only when something
                        is actually waiting. */}
                    {count > 0 && (
                      <span className={styles.navBadge} aria-label={`${String(count)} pendientes`}>
                        {count}
                      </span>
                    )}
                    {isSection && (
                      <span className={styles.navMore} aria-hidden="true">
                        ›
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        <div className={styles.sidebarFooter}>
          {/* The balance card. Only for someone who actually has one — an area
              administrator does not file requests, so it would read as zero. */}
          {holds('solicitar') && currentLive !== null && (
            <div className={styles.balanceCard}>
              <div className={styles.balanceLabel}>Saldo {currentLive.year}</div>
              <div className={styles.balanceValue}>
                <span className={styles.balanceNumber}>{currentLive.available}</span>
                <span className={styles.balanceTotal}>/ {currentLive.balance.entitledDays} días</span>
              </div>
              <div className={styles.balanceTrack}>
                <div
                  className={styles.balanceFill}
                  style={cssVars({
                    width: `${String(
                      currentLive.balance.entitledDays === 0
                        ? 0
                        : Math.min(100, (currentLive.available / currentLive.balance.entitledDays) * 100),
                    )}%`,
                  })}
                />
              </div>

              {/* Lo que queda de periodos anteriores, cada uno con su fecha. Se gasta
                  primero al pedir, así que es lo primero que conviene ver. El que
                  vence dentro de la ventana de aviso sale en ámbar, con su punto. */}
              {olderLives.map((live) =>
                live === expiring ? (
                  <div key={live.year} className={styles.balanceExpiry}>
                    <span className={styles.balanceExpiryDot} aria-hidden="true" />
                    {live.available} {live.available === 1 ? 'día' : 'días'} del {live.year}{' '}
                    {live.available === 1 ? 'vence' : 'vencen'} el {formatDayWithYear(live.lastValidDay)}
                  </div>
                ) : (
                  <div key={live.year} className={styles.balanceOlder}>
                    + {live.available} {live.available === 1 ? 'día' : 'días'} del {live.year},
                    hasta el {formatDayWithYear(live.lastValidDay)}
                  </div>
                ),
              )}
              {expiring === currentLive && (
                <div className={styles.balanceExpiry}>
                  <span className={styles.balanceExpiryDot} aria-hidden="true" />
                  {currentLive.available} {currentLive.available === 1 ? 'día vence' : 'días vencen'} el{' '}
                  {formatDayWithYear(currentLive.lastValidDay)}
                </div>
              )}
            </div>
          )}

          <div className={styles.identity}>
            <button
              type="button"
              className={styles.identityButton}
              aria-label={`Ver el perfil de ${session.displayName}`}
              onClick={() => {
                void navigate('/perfil');
              }}
            >
              <SessionAvatar />
              <span className={styles.identityText}>
                <span className={styles.identityName}>{session.displayName}</span>
                <span className={styles.identityRole}>{userScope}</span>
              </span>
            </button>
            <button
              type="button"
              className={styles.logoutButton}
              onClick={() => {
                void signOut();
              }}
            >
              Salir
            </button>
          </div>
        </div>
      </nav>

      <div className={styles.main}>
        {/* Mobile header: dark, matching the sidebar. */}
        <div className={styles.mobileBar}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => {
              setNavOpen(true);
            }}
            aria-label="Abrir menú"
            aria-expanded={navOpen}
          >
            ☰
          </button>
          <img src={logoUrl} alt="" className={styles.mobileLogo} />
          <span className={styles.mobileBrand}>{PRODUCT_NAME}</span>
          <span className={styles.mobileRole}>{roleLabel}</span>
        </div>

        <header className={styles.topbar}>
          {titleInBar && (
            <div className={styles.barTitle}>
              {/* Primero el hueco y después el título de repuesto: la hoja de
                  estilos esconde el segundo en cuanto el primero tiene algo. */}
              <div ref={setTitleNode} className={styles.barTitleSlot} />
              {fallbackTitle !== null && (
                <span className={styles.barTitleFallback}>{fallbackTitle}</span>
              )}
            </div>
          )}
          {/* Sin búsqueda su contenedor no se monta: vacío seguía reclamando el
              ancho que era para ella, y el título de al lado se quedaba sin él. */}
          {!titleInBar && (
          <div className={styles.searchWrap}>
              <div className={styles.searchField}>
                <span className={styles.searchGlyph} aria-hidden="true">
                  <SearchGlyph />
                </span>
                <input
                  type="search"
                  className={styles.searchInput}
                  aria-label="Buscar en el portal"
                  placeholder="Buscar personas, solicitudes, feriados…"
                  value={searchTerm}
                  onChange={(event) => {
                    const next = event.target.value;
                    /*
                     * The query lives in the URL, not in this component. That is
                     * what makes a result shareable and the back button correct,
                     * and it keeps the shell from owning state the search screen
                     * is the one reading.
                     */
                    void navigate(`/buscar?q=${encodeURIComponent(next)}`, {
                      replace: location.pathname === '/buscar',
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape') return;
                    event.currentTarget.blur();
                    if (location.pathname === '/buscar') void navigate(-1);
                  }}
                />
              </div>
          </div>
          )}

          <div className={styles.topbarActions}>
            {/*
              El selector de área, sólo para quien tiene más de una.
              Va junto al distintivo del rol y no dentro de cada pantalla porque
              lo que cambia es el mismo foco para todas: verlo en dos sitios
              distintos —y que discrepen— es cómo se pierde de vista cuál se está
              mirando.
            */}
            <AreaFocusPicker />
            <span className={styles.roleChip}>{roleLabel}</span>

            <div className={styles.notificationWrap}>
              <button
                type="button"
                className={styles.bellButton}
                aria-label={
                  unreadCount > 0
                    ? `Notificaciones · ${String(unreadCount)} sin leer`
                    : 'Notificaciones'
                }
                aria-haspopup="dialog"
                aria-expanded={notifOpen}
                onClick={() => {
                  setNotifOpen((open) => !open);
                }}
              >
                <BellGlyph />
                {/* Cuenta las que faltan por leer, no las que hay. Un número que
                    no baja al leerlas deja de significar «esto te espera». */}
                {unreadCount > 0 && holds('solicitar') && (
                  <span className={styles.bellBadge} aria-hidden="true">
                    {unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className={styles.notificationPanel} role="dialog" aria-label="Notificaciones">
                  <div className={styles.notificationHead}>
                    <span>Notificaciones</span>
                    <button
                      type="button"
                      className={styles.markRead}
                      onClick={() => {
                        setNotifications((current) =>
                          current.map((item) => ({ ...item, read: true })),
                        );
                        toast.ok('Marcamos todas las notificaciones como leídas.');
                      }}
                    >
                      Marcar todo leído
                    </button>
                  </div>
                  <div className={styles.notificationList}>
                    {notifications.length === 0 ? (
                      <div className={styles.notificationEmpty}>No tienes notificaciones nuevas.</div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={cx(
                            styles.notificationItem,
                            !notification.read && styles.notificationUnread,
                          )}
                        >
                          <span
                            className={cx(
                              styles.notificationIcon,
                              styles[`notificationIcon${notification.kind}`],
                            )}
                            aria-hidden="true"
                          >
                            {notification.kind === 'warning' ? '!' : notification.kind === 'success' ? '✓' : '•'}
                          </span>
                          <div className={styles.notificationBody}>
                            <strong className={styles.notificationTitle}>{notification.title}</strong>
                            <span className={styles.notificationText}>{notification.text}</span>
                            <span className={styles.notificationTime}>{notification.time}</span>
                            {notification.actionable && (
                              <div className={styles.notificationActions}>
                                <button
                                  type="button"
                                  className={styles.notificationAccept}
                                  onClick={() => {
                                    // Responder tampoco la borra: queda leída,
                                    // con la respuesta todavía a la vista.
                                    setNotifications((current) =>
                                      current.map((item) =>
                                        item.id === notification.id
                                          ? { ...item, read: true }
                                          : item,
                                      ),
                                    );
                                    toast.ok('Aceptaste el reemplazo de Bruno Chávez.');
                                  }}
                                >
                                  Aceptar reemplazo
                                </button>
                                <button
                                  type="button"
                                  className={styles.notificationDecline}
                                  onClick={() => {
                                    // Responder tampoco la borra: queda leída,
                                    // con la respuesta todavía a la vista.
                                    setNotifications((current) =>
                                      current.map((item) =>
                                        item.id === notification.id
                                          ? { ...item, read: true }
                                          : item,
                                      ),
                                    );
                                    toast.info('Avisamos que no puedes cubrir esas fechas.');
                                  }}
                                >
                                  No puedo
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <DemoRoleSwitcher />

            {/*
              * No «Nueva solicitud» here any more.
              *
              * The prototype keeps it in the top bar, and it is removed on the
              * product owner's instruction: filing lives in «Mis ausencias», next
              * to the list of what you have already asked for and the balance it
              * comes out of. A button that opens a four-step wizard from any
              * screen — including the ones where somebody is deciding other
              * people's absences — invites it by accident.
              */}
          </div>
        </header>

        {/*
          La versión nueva, avisada y no impuesta: recargar a la fuerza le quitaría a
          alguien un asistente a medio llenar, y ningún despliegue es tan urgente.

          Flota encima de todo y no va en el flujo de la página: el organigrama en
          pantalla completa tapa el marco, y un aviso que no se ve no avisa. Dice el
          atajo del teclado de quien lo lee, porque «recarga sin caché» sin decir
          cómo se queda en buena intención.
        */}
        {(needsReload || newDeployment) && (
          <div className={styles.deployNotice} role="status">
            <Banner
              tone="info"
              action={
                <Button
                  small
                  onClick={() => {
                    void reloadWithoutCache();
                  }}
                >
                  Recargar ahora
                </Button>
              }
            >
              <strong>Hay una versión nueva del portal.</strong> Recarga la página sin caché para
              verla: <kbd className={styles.kbd}>{reloadShortcut(navigator.userAgent)}</kbd>. Si
              estás capturando algo, termínalo antes.
            </Banner>
          </div>
        )}

        {configError !== null && (
          <div className={styles.updateBanner}>
            <Banner tone="warn">{configError}</Banner>
          </div>
        )}

        {/*
          Las pestañas de la sección, cuando la entrada del menú es una sección.

          Son navegación y no un control de la pantalla: cada una es una ruta, la
          de siempre. Por eso van aquí, las pinta el shell para todas igual y se
          ven distintas de las pestañas que una pantalla tenga dentro —
          subrayadas y no en cápsula—: dos filas iguales, una encima de otra, no
          dirían cuál manda.
        */}
        {section !== undefined && sectionTabs.length > 0 && (
          <nav className={styles.sectionBar} aria-label={`Secciones de ${section.label}`}>
            <span className={styles.sectionName}>{section.label}</span>
            <div className={cx(styles.sectionTabs, 'scroll-x')}>
              {sectionTabs.map((tab) => {
                const active = covers(tab.path, location.pathname);
                const count = countOf(tab);
                return (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    className={cx(styles.sectionTab, active && styles.sectionTabActive)}
                    aria-current={active ? 'page' : undefined}
                  >
                    {tab.label}
                    {count > 0 && (
                      <span
                        className={styles.sectionTabCount}
                        aria-label={`${String(count)} pendientes`}
                      >
                        {count}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}

        <main
          className={cx(styles.content, usesFullWidth(location.pathname) && styles.contentWide)}
          aria-label={current?.label ?? PRODUCT_NAME}
        >
          <PageTitleSlotContext.Provider value={titleSlot}>
            <Outlet />
          </PageTitleSlotContext.Provider>
        </main>

        {/*
          La confirmación de datos, que sale una sola vez y por encima de todo.
          Vive aquí y no en una pantalla porque no es un destino: es lo primero
          que ve quien entra, mire la ruta que mire.
        */}
        <ConfirmDataGate>
          {/* Y detrás, el día de su cumpleaños, la felicitación de la empresa. */}
          <BirthdayGreeting />
        </ConfirmDataGate>
      </div>
    </div>
  );
};

/** The magnifier, matching the prototype's inline SVG exactly. */
const SearchGlyph = (): ReactNode => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    focusable="false"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-4.3-4.3" />
  </svg>
);

const BellGlyph = (): ReactNode => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

type NotificationItem = {
  readonly id: string;
  readonly kind: 'warning' | 'success' | 'brand';
  readonly title: string;
  readonly text: string;
  readonly time: string;
  readonly actionable?: boolean;
  /**
   * Leída no es lo mismo que atendida.
   *
   * «Marcar todo leído» vaciaba la campana, así que enterarse de algo lo
   * borraba: quien quería volver a mirar qué le habían avisado ya no tenía
   * dónde. Ahora sólo se apaga el resalte y el aviso se queda donde estaba.
   */
  readonly read: boolean;
};
